import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import theme from 'core/theme/theme';

import { AuthProvider } from 'modules/auth/contexts/AuthContext';
import ProtectedRoute from 'core/routes/ProtectedRoute';
import LegalGate from 'modules/legal/LegalGate';
import Redirector from 'core/routes/Redirector';
import AppLayout from 'core/layouts/AppLayout';
import ErrorBoundary from 'shared/components/ErrorBoundary';

import Loader from 'shared/components/Loader';
import Unauthorized from 'shared/components/Unauthorized';
import SessionTimeoutManager from 'shared/components/SessionTimeoutManager';

// ================= LAZY PAGES =================

// Auth
const Login          = lazy(() => import('modules/auth/pages/Login'));
const LegalCenter    = lazy(() => import('modules/legal/pages/LegalCenter'));
const LegalManagement = lazy(() => import('modules/admin/pages/LegalManagement'));
const ChangePassword = lazy(() => import('modules/auth/pages/ChangePassword'));

// Profile
const Profile = lazy(() => import('modules/profile/Profile'));

// Employee
const EmployeeDashboard  = lazy(() => import('modules/employee/pages/EmployeeDashboard'));
const PunchHistory       = lazy(() => import('modules/employee/pages/PunchHistory'));
const PunchCorrections   = lazy(() => import('modules/employee/pages/PunchCorrections'));
const CreateAllowance    = lazy(() => import('modules/employee/pages/CreateAllowance'));
const AllowanceHistory   = lazy(() => import('modules/employee/pages/AllowanceHistory'));
const DailySummary       = lazy(() => import('modules/employee/pages/DailySummary'));
const MyCollections      = lazy(() => import('modules/employee/pages/MyCollections'));

// Admin
const AdminDashboard            = lazy(() => import('modules/admin/pages/AdminDashboard'));
const UserManagement            = lazy(() => import('modules/admin/pages/UserManagement'));
const CreateUser                = lazy(() => import('modules/admin/pages/CreateUser'));
const PendingApprovals          = lazy(() => import('modules/approvals/pages/PendingApprovals'));
const CollectionCorrectionApprovals = lazy(() => import('modules/approvals/pages/CollectionCorrectionApprovals'));
const PunchDetails              = lazy(() => import('modules/admin/pages/PunchDetails'));
const EmployeeTracking          = lazy(() => import('modules/admin/pages/EmployeeTracking'));
const CRMVisits                 = lazy(() => import('modules/admin/pages/CRMVisits'));
const ProfileApproval           = lazy(() => import('modules/admin/pages/ProfileApproval'));
const AdminDeviceManagement     = lazy(() => import('modules/admin/pages/AdminDeviceManagement'));
const PasswordManagement        = lazy(() => import('modules/admin/pages/PasswordManagement'));
const TrackingMap               = lazy(() => import('modules/admin/pages/TrackingMap'));
const CorrectionSettings        = lazy(() => import('modules/admin/pages/CorrectionSettings'));
const NotificationControl       = lazy(() => import('modules/admin/pages/NotificationControl'));
const FeatureManagement         = lazy(() => import('modules/admin/pages/FeatureManagement'));
const RoleManagement            = lazy(() => import('modules/admin/pages/RoleManagement'));
const FeatureAssignment         = lazy(() => import('modules/admin/pages/FeatureAssignment'));
const OrganizationSettings      = lazy(() => import('modules/admin/pages/OrganizationSettings'));
const CustomerLoanData          = lazy(() => import('modules/admin/pages/CustomerLoanData'));
const EmployeeData              = lazy(() => import('modules/admin/pages/EmployeeData'));
const FeatureAccessControl      = lazy(() => import('modules/admin/pages/FeatureAccessControl'));
const CollectionUpload          = lazy(() => import('modules/admin/pages/CollectionUpload'));
const CollectionPlanReport      = lazy(() => import('modules/admin/pages/CollectionPlanReport'));
const EmployeeAnalytics         = lazy(() => import('modules/admin/pages/EmployeeAnalytics'));
const CustomerProfile           = lazy(() => import('modules/admin/pages/CustomerProfile/CustomerProfile'));
const CollectionHistory         = lazy(() => import('modules/admin/pages/CollectionHistory'));
const CollectionActivityReport  = lazy(() => import('modules/admin/pages/CollectionActivityReport'));
const MasterDataManagement      = lazy(() => import('modules/admin/pages/MasterDataManagement'));
const DesignationManagement     = lazy(() => import('modules/admin/pages/DesignationManagement'));
const DistanceVerification      = lazy(() => import('modules/admin/pages/DistanceVerification'));
const VisitPlanner              = lazy(() => import('modules/admin/pages/VisitPlanner'));
const PunchReviewCenter         = lazy(() => import('modules/admin/pages/PunchReviewCenter'));
const PunchTrackingAlerts       = lazy(() => import('modules/admin/pages/PunchTrackingAlerts'));
const DuplicateCollectionRecords = lazy(() => import('modules/admin/pages/DuplicateCollectionRecords'));
const UploadHistory              = lazy(() => import('modules/admin/pages/UploadHistory'));
const UploadArchive              = lazy(() => import('modules/admin/pages/UploadArchive'));
const CustomerMap                = lazy(() => import('modules/customer-map/pages/CustomerMap'));
const EmployeeHierarchy          = lazy(() => import('modules/admin/pages/EmployeeHierarchyManagement'));
const CaseTransfer               = lazy(() => import('modules/admin/pages/CaseTransfer'));
const TeamActivity               = lazy(() => import('modules/admin/pages/TeamActivity'));

// ── Administration / Hierarchy & Approval Engine ──
const HierarchyManagement   = lazy(() => import('modules/hierarchy/pages/HierarchyManagement'));
const HierarchyQuickSetup   = lazy(() => import('modules/hierarchy/pages/HierarchyQuickSetup'));
const ApprovalConfiguration = lazy(() => import('modules/hierarchy/pages/ApprovalConfiguration'));
const ApprovalMatrix        = lazy(() => import('modules/hierarchy/pages/ApprovalMatrix'));
const ApprovalLevels        = lazy(() => import('modules/hierarchy/pages/ApprovalLevels'));
const HierarchyTree         = lazy(() => import('modules/hierarchy/pages/HierarchyTree'));
const ApprovalHistory       = lazy(() => import('modules/hierarchy/pages/ApprovalHistory'));
const Delegation            = lazy(() => import('modules/hierarchy/pages/Delegation'));
const EscalationRules       = lazy(() => import('modules/hierarchy/pages/EscalationRules'));

// ─── Role guards ─────────────────────────────────────────────────────────────
// Thin wrappers that only add a role check — no layout re-mount.
const AdminOnly      = ({ children }) => <ProtectedRoute requiredRoles={['ADMIN', 'SUPER_ADMIN']}>{children}</ProtectedRoute>;
const SuperAdminOnly = ({ children }) => <ProtectedRoute requiredRoles={['SUPER_ADMIN']}>{children}</ProtectedRoute>;
const EmployeeOnly   = ({ children }) => <ProtectedRoute requiredRoles={['EMPLOYEE']}>{children}</ProtectedRoute>;
// My Collections doubles as the manager view (assignment layer gated inside
// the page by effective role) — managers reach the same cases through their
// reporting-hierarchy scope, decided by the backend, not the route.
const CollectionsViewable = ({ children }) => <ProtectedRoute requiredRoles={['EMPLOYEE', 'ADMIN', 'SUPER_ADMIN']}>{children}</ProtectedRoute>;

// ================= ROUTES =================
const AppRoutes = () => (
    <Suspense fallback={<Loader />}>
        <Routes>

            {/* ── PUBLIC ── */}
            <Route path="/login" element={<Login />} />
            <Route path="/legal" element={<Navigate to="/legal/terms" replace />} />
            <Route path="/legal/:type" element={<LegalCenter />} />
            <Route path="/"      element={<Redirector />} />

            {/* ── NO LAYOUT ── */}
            {/* Force password change: still behind the legal gate — the
                acknowledgement is required before ANY authenticated surface. */}
            <Route path="/change-password" element={
                <ProtectedRoute><LegalGate><ChangePassword /></LegalGate></ProtectedRoute>
            } />
            <Route path="/unauthorized" element={<Unauthorized />} />

            {/* ════════════════════════════════════════════════════
                AUTHENTICATED LAYOUT — single AppLayout instance.
                Navigating between child routes keeps the sidebar
                alive; it is never unmounted or re-fetched.
            ════════════════════════════════════════════════════ */}
            <Route element={<ProtectedRoute><LegalGate><AppLayout /></LegalGate></ProtectedRoute>}>

                {/* ── Common (any role) ── */}
                <Route path="/profile" element={<Profile />} />

                {/* ── Employee ── */}
                <Route path="/employee/dashboard"       element={<EmployeeOnly><EmployeeDashboard /></EmployeeOnly>} />
                <Route path="/employee/punch-history"   element={<EmployeeOnly><PunchHistory /></EmployeeOnly>} />
                <Route path="/employee/punch-corrections" element={<EmployeeOnly><PunchCorrections /></EmployeeOnly>} />
                <Route path="/employee/create-allowance"  element={<EmployeeOnly><CreateAllowance /></EmployeeOnly>} />
                <Route path="/employee/allowance-history" element={<EmployeeOnly><AllowanceHistory /></EmployeeOnly>} />
                <Route path="/employee/daily-summary"   element={<EmployeeOnly><DailySummary /></EmployeeOnly>} />
                <Route path="/employee/my-collections"  element={<CollectionsViewable><MyCollections /></CollectionsViewable>} />
                <Route path="/employee/customer-map"    element={<EmployeeOnly><CustomerMap /></EmployeeOnly>} />

                {/* ── Admin + Super Admin ── */}
                <Route path="/admin/dashboard"          element={<AdminOnly><AdminDashboard /></AdminOnly>} />
                <Route path="/admin/pending-approvals"  element={<AdminOnly><PendingApprovals /></AdminOnly>} />
                <Route path="/admin/collection-corrections" element={<AdminOnly><CollectionCorrectionApprovals /></AdminOnly>} />
                <Route path="/admin/punch-details"      element={<AdminOnly><PunchDetails /></AdminOnly>} />
                <Route path="/admin/employee-tracking"  element={<AdminOnly><EmployeeTracking /></AdminOnly>} />
                <Route path="/admin/employee/:userId"   element={<AdminOnly><EmployeeAnalytics /></AdminOnly>} />
                <Route path="/admin/customer/:collectionId" element={<AdminOnly><CustomerProfile /></AdminOnly>} />
                <Route path="/admin/tracking"           element={<AdminOnly><TrackingMap /></AdminOnly>} />
                <Route path="/admin/punch-review"       element={<AdminOnly><PunchReviewCenter /></AdminOnly>} />
                <Route path="/admin/crm-visits"         element={<AdminOnly><CRMVisits /></AdminOnly>} />
                <Route path="/admin/profile-approval"   element={<AdminOnly><ProfileApproval /></AdminOnly>} />
                <Route path="/admin/collection-upload"  element={<AdminOnly><CollectionUpload /></AdminOnly>} />
                <Route path="/admin/collection-plan-report" element={<AdminOnly><CollectionPlanReport /></AdminOnly>} />
                <Route path="/admin/collection-history" element={<AdminOnly><CollectionHistory /></AdminOnly>} />
                <Route path="/admin/collection-activity" element={<AdminOnly><CollectionActivityReport /></AdminOnly>} />
                <Route path="/admin/visit-planner"      element={<AdminOnly><VisitPlanner /></AdminOnly>} />
                <Route path="/admin/customer-map"       element={<AdminOnly><CustomerMap /></AdminOnly>} />
                <Route path="/admin/users"              element={<AdminOnly><UserManagement /></AdminOnly>} />

                {/* ── Super Admin only ── */}
                <Route path="/admin/create-user"            element={<SuperAdminOnly><CreateUser /></SuperAdminOnly>} />
                <Route path="/admin/device-management"      element={<SuperAdminOnly><AdminDeviceManagement /></SuperAdminOnly>} />
                <Route path="/admin/password-management"    element={<SuperAdminOnly><PasswordManagement /></SuperAdminOnly>} />
                <Route path="/admin/correction-settings"    element={<SuperAdminOnly><CorrectionSettings /></SuperAdminOnly>} />
                <Route path="/admin/notification-control"   element={<SuperAdminOnly><NotificationControl /></SuperAdminOnly>} />
                <Route path="/admin/feature-management"     element={<SuperAdminOnly><FeatureManagement /></SuperAdminOnly>} />
                <Route path="/admin/role-management"        element={<SuperAdminOnly><RoleManagement /></SuperAdminOnly>} />
                <Route path="/admin/feature-assignment"     element={<SuperAdminOnly><FeatureAssignment /></SuperAdminOnly>} />
                <Route path="/admin/organization-settings"  element={<SuperAdminOnly><OrganizationSettings /></SuperAdminOnly>} />
                <Route path="/admin/customer-loan-data"     element={<SuperAdminOnly><CustomerLoanData /></SuperAdminOnly>} />
                <Route path="/admin/employee-data"          element={<SuperAdminOnly><EmployeeData /></SuperAdminOnly>} />
                <Route path="/admin/feature-access-control" element={<SuperAdminOnly><FeatureAccessControl /></SuperAdminOnly>} />
                <Route path="/admin/master-data"            element={<SuperAdminOnly><MasterDataManagement /></SuperAdminOnly>} />
                <Route path="/admin/designations"            element={<SuperAdminOnly><DesignationManagement /></SuperAdminOnly>} />
                <Route path="/admin/distance-verification"  element={<SuperAdminOnly><DistanceVerification /></SuperAdminOnly>} />
                <Route path="/admin/punch-tracking-alerts"  element={<SuperAdminOnly><PunchTrackingAlerts /></SuperAdminOnly>} />
                <Route path="/admin/duplicate-collections"  element={<SuperAdminOnly><DuplicateCollectionRecords /></SuperAdminOnly>} />
                <Route path="/admin/upload-history"         element={<SuperAdminOnly><UploadHistory /></SuperAdminOnly>} />
                <Route path="/admin/upload-archive"         element={<SuperAdminOnly><UploadArchive /></SuperAdminOnly>} />
                <Route path="/admin/legal"                  element={<SuperAdminOnly><LegalManagement /></SuperAdminOnly>} />
                <Route path="/admin/employee-hierarchy"     element={<SuperAdminOnly><EmployeeHierarchy /></SuperAdminOnly>} />
                <Route path="/admin/case-transfer"          element={<SuperAdminOnly><CaseTransfer /></SuperAdminOnly>} />
                <Route path="/admin/team-activity"          element={<SuperAdminOnly><TeamActivity /></SuperAdminOnly>} />

                {/* ── Administration: Hierarchy & Approval Engine (Super Admin only) ── */}
                <Route path="/admin/hierarchy/management"   element={<SuperAdminOnly><HierarchyManagement /></SuperAdminOnly>} />
                <Route path="/admin/hierarchy/quick-setup"  element={<SuperAdminOnly><HierarchyQuickSetup /></SuperAdminOnly>} />
                <Route path="/admin/hierarchy/configuration" element={<SuperAdminOnly><ApprovalConfiguration /></SuperAdminOnly>} />
                <Route path="/admin/hierarchy/matrix"       element={<SuperAdminOnly><ApprovalMatrix /></SuperAdminOnly>} />
                <Route path="/admin/hierarchy/levels"       element={<SuperAdminOnly><ApprovalLevels /></SuperAdminOnly>} />
                <Route path="/admin/hierarchy/tree"         element={<SuperAdminOnly><HierarchyTree /></SuperAdminOnly>} />
                <Route path="/admin/hierarchy/history"      element={<SuperAdminOnly><ApprovalHistory /></SuperAdminOnly>} />
                <Route path="/admin/hierarchy/delegation"   element={<SuperAdminOnly><Delegation /></SuperAdminOnly>} />
                <Route path="/admin/hierarchy/escalation"   element={<SuperAdminOnly><EscalationRules /></SuperAdminOnly>} />

                {/* Legacy tracking redirects */}
                <Route path="/admin/route-map"      element={<Navigate to="/admin/tracking" replace />} />
                <Route path="/admin/live-tracking"  element={<Navigate to="/admin/tracking" replace />} />

                {/* Location Exceptions + Punch Corrections merged into one page */}
                <Route path="/admin/punch-corrections"   element={<Navigate to="/admin/punch-review" replace />} />
                <Route path="/admin/location-exceptions" element={<Navigate to="/admin/punch-review" replace />} />

            </Route>

            {/* ── FALLBACK ── */}
            <Route path="*" element={<Navigate to="/" replace />} />

        </Routes>
    </Suspense>
);

// ================= MAIN APP =================
const App = () => (
    <ErrorBoundary showDetails={process.env.NODE_ENV === 'development'}>
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <AuthProvider>
                <SessionTimeoutManager />
                <AppRoutes />
            </AuthProvider>
        </ThemeProvider>
    </ErrorBoundary>
);

export default App;
